/**
 * Copyright ©2024 Drivepoint
 */

export default class ExcelUtilities {

  /**
   * Get an Excel Worksheet by name.
   */
  static async getWorksheetByName(context: Excel.RequestContext, name: string): Promise<Excel.Worksheet> {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();
    return worksheets.items.find(worksheet => worksheet.name === name);
  }

  /**
   * Set ribbon button 'enabled' state
   */
  static async setRibbonButtonEnabled(buttonId: string, enabled: boolean = true) {
    await Office.ribbon.requestUpdate({
      tabs: [
        {id: "DrivepointTab",
          groups: [
            {id: `Group.${buttonId}`, controls: [{id: buttonId, enabled}]}
          ]
        }
      ]
    });
  }

  /**
   * Create an Excel Worksheet with a given name.
   */
  static async createWorksheet(context: Excel.RequestContext, name: string): Promise<Excel.Worksheet> {
    const worksheet = context.workbook.worksheets.add(name);
    await context.sync();
    return worksheet;
  }

  /**
   * Clear an Excel Worksheet.
   */
  static async clearWorksheet(worksheet: Excel.Worksheet): Promise<void> {
    worksheet.getRange().clear();
    await worksheet.context.sync();
  }

  /**
   * Clear an Excel Worksheet by deleting rows in chunks (fallback method).
   */
  static async clearWorksheetByRowDeletion(worksheet: Excel.Worksheet): Promise<void> {
    try {
      // Get the used range to determine how many rows we need to delete
      const usedRange = worksheet.getUsedRange();
      usedRange.load("rowCount");
      await worksheet.context.sync();
      if (!usedRange || usedRange.rowCount <= 1) {
        logger.debug("No data rows to delete (only header or empty worksheet)");
        return;
      }
      const totalRows = usedRange.rowCount;
      const chunkSize = 100;
      let rowsDeleted = 0;
      logger.debug(`Starting row-by-row deletion for ${totalRows - 1} data rows in chunks of ${chunkSize}`);
      // Delete rows starting from row 2 (index 1) in chunks, header does not need to be deleted
      while (rowsDeleted < totalRows - 1) {
        const remainingRows = (totalRows - 1) - rowsDeleted;
        const currentChunkSize = Math.min(chunkSize, remainingRows);
        try {
          const rangeToDelete = worksheet.getRangeByIndexes(1, 0, currentChunkSize, 1);
          rangeToDelete.getEntireRow().delete(Excel.DeleteShiftDirection.up);
          await worksheet.context.sync();
          rowsDeleted += currentChunkSize;
          logger.debug(`Deleted ${currentChunkSize} rows, total deleted: ${rowsDeleted}/${totalRows - 1}`);
          // Small delay to prevent overwhelming Excel
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (chunkErr: any) {
          const err = new Error(`Error deleting chunk starting at row 2, size ${currentChunkSize}: ${chunkErr.message}`);
          (err as any).code = "data_import.general.worksheet.clear.error";
          throw err;
        }
      }
      logger.debug("Row-by-row deletion completed successfully");
    } catch (error: any) {
      logger.error(`clearWorksheetByRowDeletion failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Create a worksheet, or clear an existing one.
   */
  static async createOrClearWorksheet(context: Excel.RequestContext, name: string): Promise<Excel.Worksheet> {
    let worksheet: Excel.Worksheet;
    let retryCount = 0;
    const maxRetries = 3;
    while (retryCount < maxRetries) {
      try {
        worksheet = await ExcelUtilities.getWorksheetByName(context, name);
        break;
      } catch (retryErr: any) {
        retryCount++;
        logger.warn(`getWorksheetByName attempt ${retryCount} failed: ${retryErr.message}`);
        if (retryCount >= maxRetries) {
          logger.warn(`getWorksheetByName failed after ${maxRetries} attempts, creating new worksheet`);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }
    if (worksheet) {
      let clearRetryCount = 0;
      const clearMaxRetries = 3;
      let clearSucceeded = false;
      while (clearRetryCount < clearMaxRetries) {
        try {
          await ExcelUtilities.clearWorksheet(worksheet);
          clearSucceeded = true;
          break;
        } catch (clearRetryErr: any) {
          clearRetryCount++;
          logger.error(`clearWorksheet attempt ${clearRetryCount} failed: ${clearRetryErr.message}`);
          if (clearRetryCount >= clearMaxRetries) {
            logger.error(`clearWorksheet failed after ${clearMaxRetries} attempts, falling back to row-by-row deletion`);
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }
      if (!clearSucceeded) {
        try {
          await ExcelUtilities.clearWorksheetByRowDeletion(worksheet);
        } catch (fallbackErr: any) {
          logger.error(`Fallback row deletion also failed: ${fallbackErr.message}`);
          const err = new Error(`Error clearing worksheet: ${fallbackErr.message}`);
          (err as any).code = "data_import.general.worksheet.clear.error";
          throw err;
        }
      }
    } else {
      try {
        worksheet = await ExcelUtilities.createWorksheet(context, name);
      } catch (createErr: any) {
        logger.error(`Error creating worksheet: ${createErr.message}`);
        const err = new Error(`Error creating worksheet: ${createErr.message}`);
        (err as any).code = "data_import.general.worksheet.create.error";
        throw err;
      }
    }
    return worksheet;
  }

  /**
   * Make a Range from two cells representing the upper-left and lower-right corners (as simple column/row numbers).
   */
  static makeRectRange(worksheet: Excel.Worksheet, x1, y1, x2, y2): Excel.Range {
    const cell = worksheet.getCell(y1, x1);
    return cell.getBoundingRect(worksheet.getCell(y2, x2));
  }

  /**
   * Perform a numberFormat change on the given range. The format string is an Excel format string, such as "mmm-yy"
   * or "#,##0_);(#,##0)"
   */
  static async numberFormatCellsRectRange(worksheet: Excel.Worksheet, range: Excel.Range, format: string): Promise<void> {
    range.numberFormat = [[format]];
    await worksheet.context.sync();
  }

  /**
   * Perform a style formatting change on the given range. The fillColor and fontColor are standard color strings,
   * such as "white" or "#70AD47"
   */
  static async formatCellsRectRange(worksheet: Excel.Worksheet, range: Excel.Range, fillColor: string, fontColor: string): Promise<void> {
    range.format.fill.color = fillColor;
    range.format.font.color = fontColor;
    await worksheet.context.sync();
  }

  static async batchSetWorksheetValues(range: Excel.Range, data: any[], batchSize: number, tab: any, updateProgress?: (data: any) => void, cellType?: "formulas" | "values"): Promise<void> {
    logger.debug(`batchSetWorksheetValues starting with batch size ${batchSize}`);
    const worksheet = range.worksheet;
    const batchCount = Math.ceil(data.length / batchSize);
    const startMs = Date.now();
    if (updateProgress) { updateProgress({context: "range", range: tab, action: "batch", batchSize: batchSize, batchCount: batchCount}); }
    for (let index = 0; index < batchCount; index++) {
      // the starting row to restore: 0, 250, 500, etc.
      const startRow = index * batchSize;
      // the ending row to restore: 249, 499, 749, etc. (adjusted if it is greater than the actual number of rows in data)
      const endRow = Math.min(startRow + batchSize, data.length) - 1;
      const startMs = Date.now();
      if (updateProgress) { updateProgress({context: "range", range: tab, action: "batch-start", index: index, start_row: startRow, end_row: endRow}); }
      logger.debug(`batch ${index}, rows ${startRow}-${endRow} starting`);
      // create a range that matches the shape of the data being restored, and then offset by the range's row/column
      // indices to match the destination in the worksheet
      const batchRange = worksheet
        .getCell(startRow, 0)
        .getBoundingRect(worksheet.getCell(endRow, range.columnCount - 1))
        .getOffsetRange(range.rowIndex, range.columnIndex);
      const section = data.slice(startRow, endRow + 1); // Array.slice(start, end) where "end" is not inclusive
      logger.debug(`batch ${index}, length: ${JSON.stringify(section).length}`);
      cellType === "formulas" ? batchRange.formulas = section : batchRange.values = section;
      await ExcelUtilities.sync(range.context);
      logger.debug(`batch ${index}, rows ${startRow}-${endRow} complete after ${Date.now() - startMs}ms`);
      if (updateProgress) { updateProgress({context: "range", range: tab, action: "batch-end", index: index, start_row: startRow, end_row: endRow, percent: (index + 1) / batchCount}); }
    }
    logger.debug(`batchSetWorksheetValues complete after ${Date.now() - startMs}ms`);
  }

  static async batchSetWorksheetValueWithRetry(range: Excel.Range, values: any[], tab: string, updateProgress?: (data: any) => void, cellType?: "formulas" | "values"): Promise<void> {
    try {
      const STARTING_PAYLOAD_BYTES = 1048576;
      const total_payload_size = JSON.stringify(values).length;
      let size = Math.ceil(range.rowCount / Math.ceil(total_payload_size / STARTING_PAYLOAD_BYTES));
      do {
        try {
          return await ExcelUtilities.batchSetWorksheetValues(range, values, size, tab, updateProgress, cellType);
        } catch (error: any) {
          logger.warn(`failed trying to set worksheet values with batch size of ${size}: ${error.message}`);
          size = Math.ceil(size / 2);
        }
      } while (size > 5);
    } catch (error: any) {
      const err = new Error(`Could not set values for ${tab}: ${error.message}`);
      (err as any).code = "data_import.general.worksheet.insert_values.error";
      throw err;
    }
  }

  /**
   * Gets all Named Ranges in the workbook, returned as a map of name => {scope, value}. Scope can be one of ["Worksheet", "Workbook"].
   */
  static async getNamedRanges(context: Excel.RequestContext): Promise<any> {
    const names = context.workbook.names;
    names.load("items");
    await context.sync();
    return names.items.reduce((items: any, item: Excel.NamedItem) => ({...items, [item.name]: {scope: item.scope, value: item.value}}), {});
  }

  /**
   * Given a Range, split it into multiple, smaller Ranges with a maximum number of rows and columns.
   */
  static async buildRangeBatches(range: Excel.Range, maxRowCount: number, maxColumnCount: number): Promise<Excel.Range[]> {
    const batches: Excel.Range[] = [];
    const rowCount = Math.ceil(range.rowCount / maxRowCount);
    const columnCount = Math.ceil(range.columnCount / maxColumnCount);
    for (let i = 0; i < rowCount; i++) {
      const startRow = i * maxRowCount;
      const endRow = Math.min(range.rowCount, ((i + 1) * maxRowCount)) - 1;
      for (let j = 0; j < columnCount; j++) {
        const startColumn = j * maxColumnCount;
        const endColumn = Math.min(range.columnCount, ((j + 1) * maxColumnCount)) - 1;
        const batchRange = ExcelUtilities.makeRectRange(
          range.worksheet,
          startColumn + range.columnIndex,
          startRow + range.rowIndex,
          endColumn + range.columnIndex,
          endRow + range.rowIndex
        );
        batches.push(batchRange);
      }
    }
    return batches;
  }

  static async sync(context: Excel.RequestContext): Promise<void> {
    await context.sync();
    context.application.suspendApiCalculationUntilNextSync();
    context.application.suspendScreenUpdatingUntilNextSync();
  }

  static async getCalculationMode(context?: Excel.RequestContext): Promise<Excel.CalculationMode | "Automatic" | "AutomaticExceptTables" | "Manual"> {
    if (context) {
      context.application.load("calculationMode");
      await ExcelUtilities.sync(context);
      return context.application.calculationMode;
    } else {
      return Excel.run(async (context: Excel.RequestContext) => {
        context.application.load("calculationMode");
        await ExcelUtilities.sync(context);
        return context.application.calculationMode;
      });
    }
  }

  static async setCalculationMode(mode: any, context?: Excel.RequestContext): Promise<void> {
    if (context) {
      context.application.calculationMode = mode;
      await ExcelUtilities.sync(context);
    } else {
      await Excel.run(async (context: Excel.RequestContext) => {
        context.application.calculationMode = mode;
        await ExcelUtilities.sync(context);
      });
    }
  }

  static async recalculate(): Promise<void> {
    await Excel.run(async (context: Excel.RequestContext) => {
      context.application.calculate(Excel.CalculationType.recalculate);
      await ExcelUtilities.sync(context);
    });
  }

  static async withOptionalContext(closure: (context: Excel.RequestContext) => any, context?: Excel.RequestContext): Promise<any> {
    if (context) { return closure(context); }
    return Excel.run(async (context: Excel.RequestContext) => closure(context));
  }

  static async isWorksheetProtected(context: Excel.RequestContext, worksheetName: string): Promise<boolean> {
    const worksheet = await ExcelUtilities.getWorksheetByName(context, worksheetName);
    worksheet.load("protection/protected");
    await worksheet.context.sync();
    return worksheet.protection.protected;
  }

  static async recalculateTab(tabName: string) {
    logger.debug(`Started recalculating tab ${tabName}`);
    await Excel.run(async (context: Excel.RequestContext) => {
      try {
        const m_monthly_worksheet = context.workbook.worksheets.getItem(tabName);
        m_monthly_worksheet.calculate(false);
        context.workbook.save(Excel.SaveBehavior.save);
        await context.sync();
      } catch (err) {
        logger.error(err);
      } finally {
        logger.debug(`Finished recalculating tab ${tabName}`);
      }
    });
  }

  static async updateTab(data: any[], tab: string, formatHeader: boolean = true, hideTab: boolean = false) {
    const mode = await ExcelUtilities.getCalculationMode();
    await ExcelUtilities.setCalculationMode(Excel.CalculationMode.manual);
    try {
      await Excel.run(async (context: Excel.RequestContext) => {
        const worksheet = await ExcelUtilities.createOrClearWorksheet(context, tab);
        if (data.length > 0) {
          const lastColumn = data[0].length - 1;
          const lastRow = data.length - 1;
          context.workbook.application.suspendApiCalculationUntilNextSync();
          const range = ExcelUtilities.makeRectRange(worksheet, 0, 0, lastColumn, lastRow);
          range.load("rowIndex,rowCount,columnIndex,columnCount");
          await context.sync();
          await ExcelUtilities.batchSetWorksheetValueWithRetry(range, data, tab);
          if (formatHeader) {
            await ExcelUtilities.formatCellsRectRange(worksheet, ExcelUtilities.makeRectRange(worksheet, 0, 0, lastColumn, 0), "black", "white");
          }
        }
        if (hideTab) { worksheet.visibility = Excel.SheetVisibility.hidden; }
        await context.sync();
      });
    } catch (err) {
      throw err;
    } finally {
      await ExcelUtilities.setCalculationMode(mode);
    }
  }

  /**
   * Applies R-GL wide-format number formatting (`mmm-yy` on header row, `#,##0_);(#,##0)` on
   * data cells) to a tab's date columns starting at the given column index. Mirrors
   * `GeneralLedgerWorksheet.applyFormatting` so FAM v2 dimensional imports render date
   * headers as `Jan-21` (matching the primary R-GL tab) instead of raw `YYYY/MM/DD` strings.
   *
   * @param tab           Destination tab name.
   * @param rowCount      Total rows written (header + data).
   * @param colCount      Total columns written.
   * @param dateStartIndex 0-based column index where the date period columns begin (10 for R-GL).
   */
  static async applyRGLDateFormatting(
    tab: string,
    rowCount: number,
    colCount: number,
    dateStartIndex: number,
  ): Promise<void> {
    logger.debug(`[rgl-format] entry tab=${tab} rowCount=${rowCount} colCount=${colCount} dateStartIndex=${dateStartIndex}`);
    if (rowCount === 0 || colCount === 0 || dateStartIndex >= colCount) { return; }
    const dateColumnCount = colCount - dateStartIndex;
    if (dateColumnCount <= 0) { return; }
    const startColumn = ExcelUtilities.getColumnLetter(dateStartIndex + 1);
    const endColumn = ExcelUtilities.getColumnLetter(colCount);
    await Excel.run(async (context: Excel.RequestContext) => {
      const worksheet = context.workbook.worksheets.getItem(tab);
      const headerRange = worksheet.getRange(`${startColumn}1:${endColumn}1`);
      headerRange.numberFormat = [Array(dateColumnCount).fill("mmm-yy")];
      if (rowCount > 1) {
        const dataRows = rowCount - 1;
        const dataRange = worksheet.getRange(`${startColumn}2:${endColumn}${rowCount}`);
        dataRange.numberFormat = Array.from({length: dataRows}, () => Array(dateColumnCount).fill("#,##0_);(#,##0)"));
      }
      await context.sync();
    });
    logger.debug(`[rgl-format] done ${startColumn}1:${endColumn}${rowCount}`);
  }

  /**
   * Converts a column index to Excel column letter (1 = A, 2 = B, ..., 27 = AA)
   */
  static getColumnLetter(columnIndex: number): string {
    let columnLetter = "";
    let temp = columnIndex;

    while (temp > 0) {
      const remainder = (temp - 1) % 26;
      columnLetter = String.fromCharCode(65 + remainder) + columnLetter;
      temp = Math.floor((temp - 1) / 26);
    }

    return columnLetter;
  }

  /**
   * Converts an Excel column letter to a 1-based column index (A = 1, B = 2, ..., AA = 27).
   * Case-insensitive. Throws if the input contains anything other than A-Z letters.
   */
  static getColumnIndex(columnLetter: string): number {
    const upper = String(columnLetter).toUpperCase();
    if (upper.length === 0) { throw new Error(`Invalid column letter: "${columnLetter}"`); }
    let index = 0;
    for (let i = 0; i < upper.length; i++) {
      const code = upper.charCodeAt(i);
      if (code < 65 || code > 90) { throw new Error(`Invalid column letter: "${columnLetter}"`); }
      index = index * 26 + (code - 64);
    }
    return index;
  }

  /**
   * Offsets an Excel column letter by N columns (e.g. addColumnLetters("AB", 3) === "AE").
   * Negative offsets shift left; throws if the result is < column A.
   */
  static addColumnLetters(columnLetter: string, offset: number): string {
    const target = ExcelUtilities.getColumnIndex(columnLetter) + offset;
    if (target < 1) { throw new Error(`Column offset ${offset} from "${columnLetter}" falls before column A.`); }
    return ExcelUtilities.getColumnLetter(target);
  }

  /**
   * Get the last used column of a worksheet as a letter (e.g., "AZ")
   */
  static async getLastUsedColumn(worksheet: Excel.Worksheet): Promise<string> {
    const usedRange = worksheet.getUsedRange();
    usedRange.load("columnCount,columnIndex");
    await ExcelUtilities.sync(worksheet.context);

    // Calculate the last column index (0-based)
    const lastColumnIndex = usedRange.columnIndex + usedRange.columnCount - 1;

    // Convert to Excel column letter
    return ExcelUtilities.getColumnLetter(lastColumnIndex + 1); // +1 because getColumnLetter expects 1-based index
  }

  /**
   * Get a dynamic range from column K to the last used column for a specific row
   */
  static async getRowDataRange(worksheet: Excel.Worksheet, rowNumber: number, startColumn: string = "K"): Promise<Excel.Range> {
    const lastColumn = await ExcelUtilities.getLastUsedColumn(worksheet);
    return worksheet.getRange(`${startColumn}${rowNumber}:${lastColumn}${rowNumber}`);
  }

  /**
   * Get the header range from column K to the last used column
   */
  static async getHeaderRange(worksheet: Excel.Worksheet, headerRow: number = 2, startColumn: string = "K"): Promise<Excel.Range> {
    const lastColumn = await ExcelUtilities.getLastUsedColumn(worksheet);
    return worksheet.getRange(`${startColumn}${headerRow}:${lastColumn}${headerRow}`);
  }

  /**
   * Lock (protect) a worksheet
   * @param context Excel request context
   * @param worksheetName Name of the worksheet to lock
   * @returns Promise<boolean> True if successful, false otherwise
   */
  static async lockWorksheet(context: Excel.RequestContext, worksheetName: string): Promise<boolean> {
    try {
      // Check if worksheet exists
      const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
      await context.sync();
      if (worksheet.isNullObject) {
        logger.warn(`[DrivePoint] No ${worksheetName} tab present, skipping lock`);
        return false;
      }
      logger.debug(`[DrivePoint] Locking ${worksheetName} tab`);
      try {
        // Check if the tab is already protected
        worksheet.load("protection/protected");
        await context.sync();
        if (!worksheet.protection.protected) {
          worksheet.protection.protect();
          await context.sync();
          logger.debug(`[DrivePoint] ${worksheetName} tab locked successfully`);
          return true;
        } else {
          logger.debug(`[DrivePoint] ${worksheetName} tab is already locked`);
          return true;
        }
      } catch (protectionError) {
        logger.error("[DrivePoint] Error checking/updating protection state:", protectionError);
        // Try protecting anyway
        try {
          worksheet.protection.protect();
          await context.sync();
          logger.debug(`[DrivePoint] Force locked ${worksheetName} tab`);
          return true;
        } catch (forceError) {
          logger.error(`[DrivePoint] Failed to force lock ${worksheetName} tab:`, forceError);
          return false;
        }
      }
    } catch (error) {
      logger.error(`[DrivePoint] Error locking ${worksheetName} tab:`, error);
      return false;
    }
  }

  /**
   * Unlock (unprotect) a worksheet
   * @param context Excel request context
   * @param worksheetName Name of the worksheet to unlock
   * @returns Promise<boolean> True if successful, false otherwise
   */
  static async unlockWorksheet(context: Excel.RequestContext, worksheetName: string): Promise<boolean> {
    try {
      // Check if worksheet exists
      const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
      await context.sync();
      if (worksheet.isNullObject) {
        logger.warn(`[DrivePoint] No ${worksheetName} tab present, skipping unlock`);
        return false;
      }
      logger.debug(`[DrivePoint] Unlocking ${worksheetName} tab`);
      try {
        // Check if the tab is protected first
        worksheet.load("protection/protected");
        await context.sync();
        if (worksheet.protection.protected) {
          worksheet.protection.unprotect();
          await context.sync();
          logger.debug(`[DrivePoint] ${worksheetName} tab unlocked successfully`);
          return true;
        } else {
          logger.debug(`[DrivePoint] ${worksheetName} tab is already unlocked`);
          return true;
        }
      } catch (protectionError) {
        logger.error("[DrivePoint] Error checking/updating protection state:", protectionError);
        // Try unprotecting anyway
        try {
          worksheet.protection.unprotect();
          await context.sync();
          logger.debug(`[DrivePoint] Force unlocked ${worksheetName} tab`);
          return true;
        } catch (forceError) {
          logger.error(`[DrivePoint] Failed to force unlock ${worksheetName} tab:`, forceError);
          return false;
        }
      }
    } catch (error) {
      logger.error(`[DrivePoint] Error unlocking ${worksheetName} tab:`, error);
      return false;
    }
  }

  /**
   * Check if a worksheet with the given name exists in the workbook
   */
  static async isWorksheetPresent(context: Excel.RequestContext, worksheetName: string): Promise<boolean> {
    try {
      const allSheets = context.workbook.worksheets;
      allSheets.load("items/name");
      await this.sync(context);
      for (let i = 0; i < allSheets.items.length; i++) {
        if (allSheets.items[i].name === worksheetName) { return true; }
      }
      logger.warn(`No worksheet named "${worksheetName}" present`);
      return false;
    } catch (error) {
      logger.error(`Error checking for worksheet "${worksheetName}":`, error);
      return false;
    }
  }

  /**
   * Check if the currently active worksheet has the specified name
   */
  static async isOnWorksheetWithName(context: Excel.RequestContext, worksheetName: string): Promise<boolean> {
    try {
      const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
      activeWorksheet.load("name");
      await this.sync(context);
      return activeWorksheet.name === worksheetName;
    } catch (error) {
      logger.error(`Error checking if current worksheet is "${worksheetName}":`, error);
      return false;
    }
  }

}
